import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import jm, { errorMessage, formatDate, toInputDate } from './api';
import Modal from './common/Modal';
import useTableSort, { sortableHeaderProps } from './common/useTableSort';
import { StatusBadge } from './common/CandidateTable';
import Pagination from '../Pagination/Paginations';
import EntriesSelector from '../Pagination/EntriesSelector';
import DownloadButtons from '../DownloadButtons/DownloadButtons';
import DateField from './common/DateField';

/** xin_jobs.gender is a code, not a label — 0 Male, 1 Female, 2 either. */
const GENDERS = [
  { value: '0', label: 'Male' },
  { value: '1', label: 'Female' },
  { value: '2', label: 'No Preference' },
];

/** xin_jobs.minimum_experience: 0 fresh, 1-10 years, 11 for more than ten. */
const EXPERIENCE_OPTIONS = [
  { value: '0', label: 'Fresh' },
  ...Array.from({ length: 10 }, (_, i) => ({
    value: String(i + 1),
    label: `${i + 1} year${i ? 's' : ''}`,
  })),
  { value: '11', label: 'More than 10 years' },
];

/** Table columns, in display order — also what the sort helper works off. */
const COLUMNS = [
  { key: 'action', label: 'Action', sortable: false },
  { key: 'company_name', label: 'Company' },
  { key: 'job_title', label: 'Job Title' },
  { key: 'designation_name', label: 'Designation' },
  { key: 'job_type_name', label: 'Type' },
  { key: 'job_vacancy', label: 'Vacancies' },
  { key: 'date_of_closing', label: 'Closing Date' },
  { key: 'status', label: 'Status' },
];

const EMPTY_JOB = {
  company_id: '',
  job_title: '',
  designation_id: '',
  job_type: '',
  job_vacancy: 1,
  gender: '2',
  minimum_experience: '0',
  date_of_closing: '',
  short_description: '',
  long_description: '',
  status: 1,
  is_featured: 0,
};

/** Job post CRUD — the list candidates are recruited against. */
const JobPosts = () => {
  const [jobs, setJobs] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [editing, setEditing] = useState(null); // null = closed, {} = new
  const [form, setForm] = useState(EMPTY_JOB);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    return jm.jobs()
      .then((data) => setJobs(Array.isArray(data) ? data : []))
      .catch((error) => toast.error(errorMessage(error, 'Could not load the job posts')))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
    jm.companies().then(setCompanies).catch(() => setCompanies([]));
    jm.designations().then(setDesignations).catch(() => setDesignations([]));
    jm.jobTypes().then(setJobTypes).catch(() => setJobTypes([]));
  }, [reload]);

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const openNew = () => {
    setForm(EMPTY_JOB);
    setEditing({});
  };

  const openEdit = (job) => {
    setForm({
      company_id: job.company_id ?? '',
      job_title: job.job_title ?? '',
      designation_id: job.designation_id ?? '',
      job_type: job.job_type ?? '',
      job_vacancy: job.job_vacancy ?? 1,
      // '0' is Male and falsy as a string only by accident, so test explicitly.
      gender: job.gender != null && job.gender !== '' ? String(job.gender) : '2',
      minimum_experience: String(job.minimum_experience ?? '0'),
      date_of_closing: toInputDate(job.date_of_closing),
      short_description: job.short_description ?? '',
      long_description: job.long_description ?? '',
      status: job.status ?? 1,
      is_featured: job.is_featured ?? 0,
    });
    setEditing(job);
  };

  const submit = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const payload = {
        ...form,
        company_id: Number(form.company_id),
        designation_id: Number(form.designation_id),
        job_type: Number(form.job_type),
        job_vacancy: Number(form.job_vacancy),
        status: Number(form.status),
        is_featured: Number(form.is_featured),
      };

      const result = editing?.job_id
        ? await jm.updateJob(editing.job_id, payload)
        : await jm.createJob(payload);

      toast.success(result.message || 'Saved');
      setEditing(null);
      reload();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete the job post "${job.job_title}"?`)) return;

    try {
      const result = await jm.deleteJob(job.job_id);
      toast.success(result.message || 'Job post deleted');
      reload();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return jobs;

    return jobs.filter((job) =>
      [job.job_title, job.company_name, job.designation_name, job.job_type_name]
        .some((value) => String(value ?? '').toLowerCase().includes(term))
    );
  }, [jobs, search]);

  const { sorted, sort, toggle, indicator } = useTableSort(filtered, COLUMNS);

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const visible = sorted.slice((currentPage - 1) * perPage, currentPage * perPage);

  const exportRows = sorted.map((job) => ({
    Company: job.company_name ?? '',
    'Job Title': job.job_title,
    Designation: job.designation_name ?? '',
    Type: job.job_type_name ?? '',
    Vacancies: job.job_vacancy,
    'Closing Date': job.date_of_closing,
    Status: Number(job.status) === 1 ? 'Published' : 'Unpublished',
  }));

  return (
    <div className="section-body mt-3">
      <div className="container-fluid">
        <ToastContainer position="bottom-right" autoClose={3000} />

        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center">
            <h6 className="mb-0">Job Posts <span className="text-muted">({filtered.length})</span></h6>
            <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
              <DownloadButtons data={exportRows} filename="job_posts" />
              <EntriesSelector entriesPerPage={perPage} setEntriesPerPage={setPerPage} setCurrentPage={setPage} />
              <input
                className="form-control form-control-sm"
                style={{ width: 220 }}
                placeholder="Search…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
              <button type="button" className="btn btn-sm btn-primary" onClick={openNew}>+ Add New</button>
            </div>
          </div>

          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped table-bordered mb-0" style={{ fontSize: '0.82rem' }}>
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th key={column.key} {...sortableHeaderProps(column, { toggle })}>
                        {column.label}
                        {column.sortable !== false && (
                          <span
                            className="ml-1"
                            style={{
                              fontSize: '0.7rem',
                              opacity: sort.key === column.key ? 1 : 0.35,
                            }}
                          >
                            {indicator(column.key)}
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={COLUMNS.length} className="text-center py-4">Loading…</td></tr>}
                  {!loading && visible.length === 0 && (
                    <tr><td colSpan={COLUMNS.length} className="text-center py-4 text-muted">No job posts yet.</td></tr>
                  )}
                  {!loading && visible.map((job) => (
                    <tr key={job.job_id}>
                      <td>
                        <div className="d-flex" style={{ gap: 4 }}>
                          <button type="button" className="btn btn-outline-secondary btn-sm" title="Edit" onClick={() => openEdit(job)}>
                            <i className="fa fa-pencil" />
                          </button>
                          <button type="button" className="btn btn-outline-secondary btn-sm" title="Delete" onClick={() => remove(job)}>
                            <i className="fa fa-trash" />
                          </button>
                        </div>
                      </td>
                      <td>{job.company_name || '—'}</td>
                      <td>{job.job_title}</td>
                      <td>{job.designation_name || '—'}</td>
                      <td>{job.job_type_name || '—'}</td>
                      <td>{job.job_vacancy}</td>
                      <td>{formatDate(job.date_of_closing)}</td>
                      <td>
                        <StatusBadge
                          tone={Number(job.status) === 1 ? 'success' : 'secondary'}
                          label={Number(job.status) === 1 ? 'Published' : 'Unpublished'}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>

        <Modal
          show={!!editing}
          title={editing?.job_id ? 'Edit Job Post' : 'Add Job Post'}
          onClose={() => setEditing(null)}
          onSubmit={submit}
          saving={saving}
        >
          <div className="row">
            <div className="col-md-6 form-group required-tooltip">
              <label>Company</label>
              <select className="form-control required-input" value={form.company_id} onChange={(e) => set('company_id', e.target.value)}>
                <option value="">Select</option>
                {companies.map((company) => (
                  <option key={company.company_id} value={company.company_id}>{company.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6 form-group required-tooltip">
              <label>Job Title</label>
              <input className="form-control required-input" value={form.job_title} onChange={(e) => set('job_title', e.target.value)} />
            </div>
            <div className="col-md-6 form-group required-tooltip">
              <label>Designation</label>
              <select className="form-control required-input" value={form.designation_id} onChange={(e) => set('designation_id', e.target.value)}>
                <option value="">Select</option>
                {designations.map((designation) => (
                  <option key={designation.designation_id} value={designation.designation_id}>
                    {designation.designation_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6 form-group required-tooltip">
              <label>Job Type</label>
              <select className="form-control required-input" value={form.job_type} onChange={(e) => set('job_type', e.target.value)}>
                <option value="">Select</option>
                {jobTypes.map((type) => (
                  <option key={type.job_type_id} value={type.job_type_id}>{type.type}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 form-group required-tooltip">
              <label>Vacancies</label>
              <input type="number" min="1" className="form-control required-input" value={form.job_vacancy} onChange={(e) => set('job_vacancy', e.target.value)} />
            </div>
            <div className="col-md-4 form-group">
              <label>Gender</label>
              <select className="form-control" value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                {GENDERS.map((gender) => (
                  <option key={gender.value} value={gender.value}>{gender.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-4 form-group">
              <label>Minimum Experience</label>
              <select className="form-control" value={form.minimum_experience} onChange={(e) => set('minimum_experience', e.target.value)}>
                {EXPERIENCE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-6 form-group required-tooltip">
              <label>Closing Date</label>
              <DateField value={form.date_of_closing} onChange={(v) => set('date_of_closing', v)} required />
            </div>
            <div className="col-md-6 form-group">
              <label>Status</label>
              <select className="form-control" value={form.status} onChange={(e) => set('status', e.target.value)}>
                <option value={1}>Published</option>
                <option value={2}>Unpublished</option>
              </select>
            </div>
            <div className="col-md-12 form-group required-tooltip">
              <label>Short Description</label>
              <textarea className="form-control required-input" rows="2" value={form.short_description} onChange={(e) => set('short_description', e.target.value)} />
            </div>
            <div className="col-md-12 form-group required-tooltip">
              <label>Long Description</label>
              <textarea className="form-control required-input" rows="4" value={form.long_description} onChange={(e) => set('long_description', e.target.value)} />
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default JobPosts;
